import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Switch } from "../components/ui/switch";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { Badge } from "../components/ui/badge";
import {
  Plus,
  Search,
  MoreHorizontal,
  Pencil,
  Trash2,
  Download,
  Radio,
  ExternalLink,
} from "lucide-react";
import { toast } from "sonner";

export default function DSPListPage() {
  const { api } = useAuth();
  const navigate = useNavigate();
  const [dsps, setDsps] = useState([]);
  const [dspQPS, setDspQPS] = useState({});
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [deleteId, setDeleteId] = useState(null);

  useEffect(() => {
    fetchDSPs();
    fetchQPS();
    
    // Auto-refresh QPS every 10 seconds
    const interval = setInterval(fetchQPS, 10000);
    return () => clearInterval(interval);
  }, []);

  const fetchDSPs = async () => {
    try {
      const response = await api.get("/dsps");
      setDsps(response.data);
    } catch (error) {
      toast.error("Failed to fetch DSPs");
    } finally {
      setLoading(false);
    }
  };

  const fetchQPS = async () => {
    try {
      const response = await api.get("/qps/by-dsp?period=today");
      // Create a map of endpoint_id to QPS data
      const qpsMap = {};
      response.data.forEach(item => {
        qpsMap[item.endpoint_id] = item;
      });
      setDspQPS(qpsMap);
    } catch (error) {
      // Silent fail for QPS
    }
  };

  const handleDelete = async () => {
    try {
      await api.delete(`/dsps/${deleteId}`);
      setDsps(dsps.filter((d) => d.id !== deleteId));
      toast.success("DSP deleted successfully");
    } catch (error) {
      toast.error("Failed to delete DSP");
    } finally {
      setDeleteId(null);
    }
  };

  const handleExport = async (format) => {
    try {
      const response = await api.get(`/export/dsps?format=${format}`, {
        responseType: format === "csv" ? "blob" : "json",
      });
      
      if (format === "csv") {
        const blob = new Blob([response.data], { type: "text/csv" });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = "dsps.csv";
        a.click();
      } else {
        const blob = new Blob([JSON.stringify(response.data, null, 2)], {
          type: "application/json",
        });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = "dsps.json";
        a.click();
      }
      toast.success(`DSPs exported as ${format.toUpperCase()}`);
    } catch (error) {
      toast.error("Failed to export DSPs");
    }
  };

  const toggleStatus = async (dsp) => {
    const newStatus = dsp.status === "active" ? "paused" : "active";
    try {
      await api.put(`/dsps/${dsp.id}`, { status: newStatus });
      setDsps(dsps.map(d => d.id === dsp.id ? { ...d, status: newStatus } : d));
      toast.success(`DSP ${newStatus === "active" ? "activated" : "paused"}`);
    } catch (error) {
      toast.error("Failed to update status");
    }
  };

  const filteredDSPs = dsps.filter(
    (dsp) =>
      dsp.name.toLowerCase().includes(search.toLowerCase()) ||
      dsp.company_name?.toLowerCase().includes(search.toLowerCase())
  );

  const getStatusBadge = (status) => {
    const variants = {
      active: "bg-emerald-500/10 text-emerald-500 hover:bg-emerald-500/20",
      inactive: "bg-zinc-500/10 text-zinc-500 hover:bg-zinc-500/20",
      paused: "bg-amber-500/10 text-amber-500 hover:bg-amber-500/20",
    };
    return (
      <Badge className={variants[status] || variants.inactive} variant="outline">
        {status}
      </Badge>
    );
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="dsp-list-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2">
            <Radio className="h-8 w-8 text-accent" />
            DSP Endpoints
          </h1>
          <p className="text-muted-foreground">
            Manage your Demand-Side Platform endpoints
          </p>
        </div>
        <div className="flex items-center gap-2">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" data-testid="export-btn">
                <Download className="mr-2 h-4 w-4" />
                Export
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent>
              <DropdownMenuItem onClick={() => handleExport("json")}>
                Export as JSON
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleExport("csv")}>
                Export as CSV
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <Link to="/dsps/new">
            <Button data-testid="create-dsp-btn">
              <Plus className="mr-2 h-4 w-4" />
              Create DSP
            </Button>
          </Link>
        </div>
      </div>

      {/* Search */}
      <div className="relative max-w-sm">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="Search DSPs..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-9"
          data-testid="search-input"
        />
      </div>

      {/* Table */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-accent"></div>
        </div>
      ) : filteredDSPs.length === 0 ? (
        <div className="text-center py-12 border rounded-lg" data-testid="empty-state">
          <Radio className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
          <h3 className="text-lg font-semibold mb-2">No DSPs found</h3>
          <p className="text-muted-foreground mb-4">
            {search ? "Try a different search term" : "Create your first DSP endpoint"}
          </p>
          {!search && (
            <Link to="/dsps/new">
              <Button>
                <Plus className="mr-2 h-4 w-4" />
                Create DSP
              </Button>
            </Link>
          )}
        </div>
      ) : (
        <div className="border rounded-lg overflow-hidden">
          <Table data-testid="dsp-table">
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Company</TableHead>
                <TableHead>OpenRTB</TableHead>
                <TableHead className="text-right">In QPS</TableHead>
                <TableHead className="text-right">Out QPS</TableHead>
                <TableHead className="text-right">Bid Rate</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredDSPs.map((dsp) => {
                const qps = dspQPS[dsp.id] || {};
                return (
                <TableRow key={dsp.id} data-testid={`dsp-row-${dsp.id}`}>
                  <TableCell>
                    <div>
                      <p className="font-medium">{dsp.name}</p>
                      <a
                        href={dsp.endpoint_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-xs text-muted-foreground hover:text-accent flex items-center gap-1"
                      >
                        <ExternalLink className="h-3 w-3" />
                        Endpoint
                      </a>
                    </div>
                  </TableCell>
                  <TableCell>{dsp.company_name}</TableCell>
                  <TableCell>
                    <Badge variant="secondary" className="font-mono text-xs">
                      v{dsp.ortb_version || "2.5"}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right">
                    <span className="font-mono text-sm text-blue-600">
                      {qps.incoming_qps ? qps.incoming_qps.toFixed(0) : "-"}
                    </span>
                  </TableCell>
                  <TableCell className="text-right">
                    <span className="font-mono text-sm text-purple-600">
                      {qps.outgoing_qps ? qps.outgoing_qps.toFixed(0) : "-"}
                    </span>
                  </TableCell>
                  <TableCell className="text-right">
                    <Badge variant={qps.bid_rate > 50 ? "default" : "secondary"}>
                      {qps.bid_rate ? `${qps.bid_rate}%` : "-"}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <Switch
                        checked={dsp.status === "active"}
                        onCheckedChange={() => toggleStatus(dsp)}
                        data-testid={`dsp-status-toggle-${dsp.id}`}
                      />
                      <span className={`text-xs font-medium ${dsp.status === "active" ? "text-green-600" : "text-orange-500"}`}>
                        {dsp.status === "active" ? "Active" : "Paused"}
                      </span>
                    </div>
                  </TableCell>
                  <TableCell className="text-right">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon" data-testid={`dsp-actions-${dsp.id}`}>
                          <MoreHorizontal className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => navigate(`/dsps/${dsp.id}/edit`)}>
                          <Pencil className="mr-2 h-4 w-4" />
                          Edit
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={() => setDeleteId(dsp.id)}
                          className="text-destructive"
                        >
                          <Trash2 className="mr-2 h-4 w-4" />
                          Delete
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              )})}
            </TableBody>
          </Table>
        </div>
      )}

      {/* Delete Dialog */}
      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete DSP</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this DSP endpoint? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-destructive text-destructive-foreground">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
